import { workDayKey } from '../lib/format';
import type { NodeId, Permissions, SearchScope, TagId } from '../lib/types';
import type { Camera } from './slices/camera';
import { DEFAULT_SEARCH_SCOPE } from './slices/filters';
import { DEFAULT_SIDEBAR, MAX_SIDEBAR, MIN_SIDEBAR } from './slices/ui';

/**
 * Every browser storage key this app touches, and the exact semantics of each.
 *
 * FOUR KEYS, THREE DIFFERENT LIFETIMES. That is the whole design, and getting one of them
 * wrong is a bug a user experiences as the app forgetting something it should have kept, or
 * remembering something it should have dropped:
 *
 *  - `STORE` (`conigon_infostation_v1`) — DURABLE prefs, shared by every user of this browser
 *    and never expiring: theme, sidebar, edit mode, search scope, the minimap. Resetting
 *    somebody's dark mode overnight reads as a bug, so these do not expire, and they are not
 *    namespaced per user because a browser's appearance is the browser's.
 *  - `VIEWSTORE` (`conigon_infostation_view_v1`) — the day-scoped VIEW state, namespaced per
 *    USER id: which branches are expanded, the filters, the camera, AI-doc mode. See the work
 *    day rule below. Sign-out clears the leaving user's record (`clearViewRecord`, called
 *    from `logout()`); session expiry deliberately does not, because nobody chose to leave.
 *  - `USER_PERMS_KEY` (`userPermissions`, in **sessionStorage**) — the suite-wide integration
 *    contract. Other CONIGON apps read this object; its shape is not ours to change freely,
 *    and this app owns exactly one key inside it (`documentation`), leaving the rest alone.
 *  - `DOCNAV_STORE` (**sessionStorage**) — a single-boolean UI toggle (the doc overlay's
 *    navigation band), remembered per tab and gone when it closes. It holds no personal data.
 *    The constant lives HERE and is imported by its owning region, so this file's claim to name
 *    every key is enforced by the import graph rather than by a comment.
 *    `AIPANEL_STORE` was its twin until V0.51: the node popup's AI `<details>` remembered
 *    itself per TAB while every other popup fold remembered itself per USER and per work day.
 *    One rule now covers all of them (`ViewRecord.openSections`), so the key is gone rather
 *    than left writing a value nothing reads. Four keys, three lifetimes.
 *
 * THE WORK DAY RULE. A view belongs to the work day that contains it, where a work day runs
 * 04:00 -> 03:59 local (`workDayKey`, `lib/format.ts`). On load, restore only if the stored
 * record was saved on the SAME work day as now; otherwise drop it and open collapsed,
 * unfiltered and centred. Not midnight, because somebody working late would have the map
 * collapse under them the moment the date rolled over. Not an inactivity window, because that
 * gets the intent wrong in both directions: twelve hours at the same desk is still one working
 * day, while finishing at 01:00 and returning at 09:00 is eight hours and IS a new day.
 *
 * EVALUATED ONLY AT LOAD. Never on a timer — a timer could fire mid-session and collapse the
 * map under somebody at 04:00.
 *
 * NO KEY MAY CHANGE without bumping the constant AND handling the old data (CLAUDE.md). The
 * keys are byte-for-byte the ones `infostation.html` used, and the record shapes below are
 * what it wrote — which is what let the two implementations read each other's data through the
 * migration, and what lets an existing browser come back to its own view after the flip.
 * One FIELD has since been dropped rather than a key changed: `ViewRecord.username` (RGPD —
 * a plaintext identity that outlived sign-out). It was always optional, so an old record
 * still parses and simply loses the field on its next write; no key bump needed.
 */

/** Durable, browser-wide. Renamed from `conigon_mindmap_v3` at the InfoStation v0.1 relaunch. */
export const STORE = 'conigon_infostation_v1';
/** Day-scoped, per user. */
export const VIEWSTORE = 'conigon_infostation_view_v1';
/** sessionStorage. The suite-wide contract — other apps read this. */
export const USER_PERMS_KEY = 'userPermissions';
/** sessionStorage. The doc overlay's navigation band: expanded or not, per tab. Owned by
    `features/docview/DocOverlay.tsx`, which imports the name from here. */
export const DOCNAV_STORE = 'infostation_docnav_expanded';
/* `AIPANEL_STORE` ('infostation_aipanel_open') was here until V0.51 — the node popup's AI
   `<details>`, remembered per TAB. Every popup fold is remembered per USER and per work day now
   (`ViewRecord.openSections`), so the key is deleted rather than left behind writing a value
   nothing reads. A tab that still holds the old key simply keeps it until it closes. */
/** Cap the per-user records so a shared machine cannot grow this key forever. */
export const VIEW_KEEP_USERS = 8;

/**
 * Storage, if there is any.
 *
 * Looked up per call rather than captured at module scope: this module is imported by tests
 * that install a fake, and by a bundle that must not throw during evaluation in an environment
 * without a DOM. Every caller below also tolerates a throw — Safari in private mode and a full
 * quota both raise from `setItem`, and losing a preference must never break a save.
 */
function storage(kind: 'local' | 'session'): Storage | null {
  try {
    const s = kind === 'local' ? globalThis.localStorage : globalThis.sessionStorage;
    return s ?? null;
  } catch {
    return null;
  }
}

/* ---------------------------------------------------------------- durable prefs (STORE) */

/**
 * NOT HERE: `showLinks` (V0.48.1, at the user's request). The dotted cross-links are OFF at
 * the start of every session and the toggle is the reader's answer for that session alone —
 * so there is nothing durable to hold, and a record written before this simply carries a
 * field nothing reads any more. Durable is a decision about LIFETIME, not about importance:
 * this is the one preference whose right default does not depend on what you did last time.
 */
export interface DurablePrefs {
  isLight: boolean;
  sidebarOpen: boolean;
  sidebarWidth: number;
  editing: boolean;
  searchScope: SearchScope;
  /**
   * The minimap (V0.53.4). HERE, unlike `showLinks`, because its right default does not change
   * with the day: somebody who switched it off did so for the screen they have, and it would
   * be back the next morning otherwise. Defaults ON — a record written before it existed has
   * no field, and the minimap is meant to be there until somebody says otherwise.
   */
  minimapOn: boolean;
  /**
   * The minimap keeps its bright level instead of fading when the camera rests (V0.54, from
   * the minimap's right-click menu). Durable for `minimapOn`'s reason — it is a statement about
   * this person's screen, not about today. Defaults OFF: the fade is the designed behaviour,
   * and a record written before the field existed has no answer, which means "as designed".
   */
  minimapBright: boolean;
}

export const DEFAULT_PREFS: DurablePrefs = {
  isLight: false,
  sidebarOpen: true,
  sidebarWidth: DEFAULT_SIDEBAR,
  editing: false,
  searchScope: DEFAULT_SEARCH_SCOPE,
  minimapOn: true,
  minimapBright: false,
};

/**
 * Read the durable preferences.
 *
 * Defaults are applied field by field with the vanilla's own polarity, which is not uniform
 * and cannot be simplified: `sidebarOpen` defaults to TRUE and so tests `!== false`, while
 * `isLight` and `editing` default to false and so test truthiness. Writing them all one way
 * would flip a setting for everybody with an existing record.
 *
 * THE SCOPE IS SANITISED FIELD BY FIELD against today's shape (plan 2.2): the four text
 * scopes — `content` included — each default ON, and the retired presence facets
 * (doc/internal/course) an old record may still carry are silently dropped, never copied.
 * Same key, no bump: the record parses either way and takes the new shape on its next write.
 * The doc-KIND filter is transient and deliberately not stored here at all — exactly the
 * property the old facets had.
 *
 * `s.view` in an old record is IGNORED. The camera moved to the day-scoped record in V0.24;
 * records written before then still carry it, and the only effect of skipping it is one
 * re-centre on the first load after upgrading.
 */
export function readDurablePrefs(): DurablePrefs {
  const s = storage('local');
  if (!s) return DEFAULT_PREFS;
  let raw: unknown;
  try {
    raw = JSON.parse(s.getItem(STORE) ?? 'null');
  } catch {
    return DEFAULT_PREFS;
  }
  if (!raw || typeof raw !== 'object') return DEFAULT_PREFS;
  const r = raw as Partial<DurablePrefs>;
  const scope = (r.searchScope ?? DEFAULT_SEARCH_SCOPE) as Partial<SearchScope>;
  return {
    isLight: !!r.isLight,
    sidebarOpen: r.sidebarOpen !== false,
    editing: !!r.editing,
    sidebarWidth: Math.max(MIN_SIDEBAR, Math.min(MAX_SIDEBAR, r.sidebarWidth || DEFAULT_SIDEBAR)),
    // `!== false`, the sidebar's polarity: absent on every pre-V0.53.4 record, and absent means ON.
    minimapOn: r.minimapOn !== false,
    // Truthiness, the theme's polarity: absent on every pre-V0.54 record, and absent means OFF.
    minimapBright: !!r.minimapBright,
    searchScope: {
      name: scope.name !== false,
      desc: scope.desc !== false,
      tags: scope.tags !== false,
      // Absent on every pre-2.2 record, so `!== false` is what makes it default ON.
      content: scope.content !== false,
    },
  };
}

/** Write the durable preferences. Swallows a throw: a lost preference is not a lost edit. */
export function writeDurablePrefs(prefs: DurablePrefs): void {
  const s = storage('local');
  if (!s) return;
  try {
    s.setItem(STORE, JSON.stringify(prefs));
  } catch {
    /* quota, private mode — the app carries on */
  }
}

/* --------------------------------------------------------- day-scoped view (VIEWSTORE) */

export interface ViewRecord {
  // NOTE a `username` field used to sit here "for eyeballing localStorage". It was dropped
  // (RGPD): a plaintext identity in a durable store outlives sign-out for no functional gain —
  // the record is keyed on the user ID. It was always optional, so old records still parse;
  // the field disappears from each record on its next write.
  savedAt: number;
  /** The work day this was saved on, `YYYY-MM-DD`. The whole expiry rule. */
  day: string;
  view: Camera;
  /**
   * Only the EXCEPTIONS to "collapsed".
   *
   * Storing the expanded ids rather than the collapsed ones is what makes a node added since
   * the last visit default to collapsed instead of springing open — and it makes an id that
   * has since been deleted a no-op on restore rather than an error.
   */
  openIds: NodeId[];
  /**
   * The selected filter tags (plan 2.3's multi-select). A record written before the reshape
   * carried `activeTag: TagId | null` instead; `readViewRecord` wraps that old field into a
   * one-element list as it reads — no key bump, and day-scoped records age out within a day
   * anyway.
   */
  activeTags: TagId[];
  searchQuery: string;
  aiDocMode: boolean;
  /**
   * The clearance heatmap (V0.39) — day-scoped beside `aiDocMode`, same shape and rules.
   * OPTIONAL because records written before V0.39 do not carry it, and absent reads as off;
   * same key, no bump — the record takes the field on its next write.
   */
  heatmapOn?: boolean;
  /**
   * The documentation rows pinned open (V0.42.2) — day-scoped beside `heatmapOn`, which is
   * what switches it on. OPTIONAL because older records do not carry it, and absent reads as
   * off; same key, no bump — the record takes the field on its next write.
   */
  docsPinned?: boolean;
  /**
   * The pipeline-stage filter (a PIPELINE_DISPLAY key since V0.38; the FIELD keeps its
   * historical name — same key, no bump). A record written before the unification may still
   * carry a retired docStatus value ('proofread', 'doc_generated'…): this layer stores and
   * returns it untouched, and `restoreViewState()` validates against the current stage table,
   * restoring an unknown value as "no filter" rather than a filter nothing can match. The
   * VIEWSTORE is day-scoped, so stale values age out on their own within a work day anyway.
   */
  activeDocStatus: string | null;
  /**
   * WHICH SECTIONS ARE FOLDED SHUT (V0.50) — the node popup's To-dos section, and the four
   * headings in the "My items" pane.
   *
   * THE EXCEPTIONS ARE STORED, not the state, exactly as `openIds` above stores the expanded
   * branches rather than the collapsed ones — and for the mirrored reason. There the default is
   * collapsed, so the open ones are the exceptions; here the default is OPEN, so the shut ones
   * are. A section added in a later release therefore defaults to open rather than inheriting
   * somebody's answer about a section that did not exist, and a key for a section since removed
   * is a no-op on restore instead of an error.
   *
   * DAY-SCOPED, like everything else in this record. A section folded shut yesterday is open
   * again this morning, which is the same rule the map's own branches follow — and the reason
   * this went here rather than into `DurablePrefs`: those are browser-wide and not namespaced
   * per user, so one person's folded pane would be everybody's on a shared machine.
   *
   * OPTIONAL, because records written before V0.50 do not carry it; absent reads as "nothing
   * folded". Same key, no bump — the record takes the field on its next write.
   */
  collapsedSections?: string[];
  /**
   * WHICH POPUP SECTIONS THIS USER HAS OPENED (V0.51) — the node popup's folds, by key
   * (`popup:responsibles`, `popup:docs`, `popup:tags`, `popup:todos`, `popup:ai`, …).
   *
   * A SECOND LIST RATHER THAN A SECOND MEANING FOR THE FIRST. `collapsedSections` above stores
   * the exceptions to OPEN, because the "My items" headings default open. The popup's folds
   * default SHUT (V0.51: a node is a page of answers, and you open the section you work in), so
   * their exceptions are the OPENED ones. Both lists could have been one map of key → boolean,
   * and that map would have been a field whose meaning depended on which key you read — so
   * there are two, each named for exactly what it holds.
   *
   * THE POINT OF STORING IT AT ALL is that a fold opened on one node is open on the NEXT node
   * opened: somebody working on documentation wants the Documentation section every time,
   * without re-opening it per node. Per user, and day-scoped like the rest of this record — a
   * new work day starts every fold shut again, which is the rule's own default.
   *
   * OPTIONAL, because records written before V0.51 do not carry it; absent reads as "nothing
   * opened". Same key, no bump — the record takes the field on its next write. A key for a
   * section since removed is a no-op on restore, exactly as above.
   */
  openSections?: string[];
}

interface ViewStoreFile {
  v: 1;
  users: Record<string, ViewRecord>;
}

function readViewStoreFile(): ViewStoreFile {
  const s = storage('local');
  if (!s) return { v: 1, users: {} };
  try {
    const parsed = JSON.parse(s.getItem(VIEWSTORE) ?? 'null') as ViewStoreFile | null;
    if (parsed && parsed.users) return parsed;
  } catch {
    /* corrupt record — start clean rather than refusing to boot */
  }
  return { v: 1, users: {} };
}

function writeViewStoreFile(file: ViewStoreFile): void {
  const s = storage('local');
  if (!s) return;
  try {
    s.setItem(VIEWSTORE, JSON.stringify(file));
  } catch {
    /* see writeDurablePrefs */
  }
}

/**
 * This user's view record, or null if there is none FOR TODAY'S WORK DAY.
 *
 * A record from a previous work day is DELETED as it is read, not merely ignored: leaving it
 * would mean a stale record sitting in storage indefinitely for anybody who signs in once and
 * never returns, and it would make "is there a record" and "will it be used" two different
 * questions.
 *
 * `now` is a parameter so the day boundary is testable without moving the clock.
 */
export function readViewRecord(userId: string, now: number = Date.now()): ViewRecord | null {
  if (!userId) return null;
  const file = readViewStoreFile();
  const rec = file.users[userId];
  if (!rec) return null;
  if (rec.day !== workDayKey(now)) {
    delete file.users[userId];
    writeViewStoreFile(file);
    return null;
  }
  // Tolerate the pre-2.3 single-tag shape: wrap `activeTag` into a one-element list. The
  // legacy field is dropped by the record's next write, exactly like `username` was.
  if (!Array.isArray(rec.activeTags)) {
    const legacy = (rec as { activeTag?: TagId | null }).activeTag;
    rec.activeTags = legacy ? [legacy] : [];
  }
  return rec;
}

/**
 * Persist this user's view state.
 *
 * DELIBERATELY NOT PART OF THE SERVER SYNC, and deliberately without a "Saved" badge. This is
 * personal view state, never map content, and it is written on every search keystroke —
 * routing it through the durable save would mean a `PUT /api/map` per character typed.
 *
 * Callers must have checked `viewWritesAllowed` first. That guard is not repeated here on
 * purpose: this function's job is the storage format, and a second copy of the boot rule is a
 * second thing that can disagree with the first.
 */
export function writeViewRecord(
  userId: string,
  rec: Omit<ViewRecord, 'savedAt' | 'day'>,
  now: number = Date.now(),
): void {
  if (!userId) return;
  const file = readViewStoreFile();
  file.users[userId] = { savedAt: now, day: workDayKey(now), ...rec };
  // Keep only the most recent handful of users on this browser.
  const ids = Object.keys(file.users).sort(
    (a, b) => (file.users[b].savedAt || 0) - (file.users[a].savedAt || 0),
  );
  for (const id of ids.slice(VIEW_KEEP_USERS)) delete file.users[id];
  writeViewStoreFile(file);
}

/**
 * Drop this user's view record. Sign-out hygiene (RGPD): an EXPLICIT sign-out on a shared
 * browser should not leave the leaving user's expanded branches, filters and camera lying in
 * localStorage for the next person to inspect. Called from `logout()` and nowhere else —
 * session expiry deliberately does not clear, because nobody chose to leave.
 *
 * Every other user's record is untouched, for the same reason `readViewRecord` expires them
 * one at a time.
 */
export function clearViewRecord(userId: string): void {
  if (!userId) return;
  const file = readViewStoreFile();
  if (!(userId in file.users)) return;
  delete file.users[userId];
  writeViewStoreFile(file);
}

/* -------------------------------------------------- suite contract (USER_PERMS_KEY) */

/**
 * Publish this app's permission payload under `userPermissions.documentation`.
 *
 * THE SHAPE IS NOT OURS. `userPermissions` is a sessionStorage object the surrounding CONIGON
 * suite shares: other apps write their own keys into it (attributes, languages, …) and read
 * each other's. So this reads the object, replaces exactly one key, and writes it back —
 * never `setItem` with a fresh object, which would delete another app's data.
 *
 * `permissionsUpdatedAt` is part of the contract too, and is refreshed on every write.
 */
export function publishUserPermissions(perm: Permissions | null): void {
  const s = storage('session');
  if (!s) return;
  let obj: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(s.getItem(USER_PERMS_KEY) ?? 'null');
    if (parsed && typeof parsed === 'object') obj = parsed as Record<string, unknown>;
  } catch {
    obj = {};
  }
  obj.permissionsUpdatedAt = new Date().toISOString();
  obj.documentation = perm;
  try {
    s.setItem(USER_PERMS_KEY, JSON.stringify(obj));
  } catch {
    /* see writeDurablePrefs */
  }
}

/** Sign-out: drop OUR key and leave every other app's alone. */
export function clearUserPermissions(): void {
  const s = storage('session');
  if (!s) return;
  try {
    const parsed = JSON.parse(s.getItem(USER_PERMS_KEY) ?? 'null');
    const obj = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    delete obj.documentation;
    s.setItem(USER_PERMS_KEY, JSON.stringify(obj));
  } catch {
    /* nothing to clear, or nothing we can do about it */
  }
}
